import { Box, Button, Checkbox, Divider, Stack, Typography } from '@mui/material';
import { Camera360Icon, SeatIcon } from '@tix-ticketing/ui-icons/filled';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { Bullet } from '../components/bullet';
import { TixlyIconButton } from '../components/button/tixly-icon-button';
import { RowSeatInfo } from '../components/row-seat-info';
import { StrikethroughText } from '../components/text/strikethrough-text';
import type { TicketTypeOption } from '../components/ticket-type-selector';
import { TicketTypeSelector } from '../components/ticket-type-selector';
import type { CurrencyData, TicketData } from '../types';
import { FormattedPrice } from '../utils/formatted-price';
const AnonymousCustomerId = 792;
type SelectableRegionProps = {
  role?: 'button';
  tabIndex?: number;
  'aria-label'?: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
};
const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

// Renders nothing without a handler, so a card that is not part of a selection
// keeps no checkbox and no extra branch in TicketCard.
const SelectionCheckbox = ({
  onToggle,
  selected,
  label
}: {
  onToggle?: () => void;
  selected?: boolean;
  label?: string;
}) => onToggle ? <Checkbox checked={!!selected} onChange={onToggle} slotProps={{
  input: {
    'aria-label': label
  }
}} sx={{
  flexShrink: 0
}} /> : <></>;

// Turns the seat details into an activatable region. Returns nothing without a
// handler, so a card that is not selectable keeps no role, no focus stop and no
// pointer cursor.
function selectableRegionProps(onSelect: (() => void) | undefined, intl: IntlShape, ticket: TicketData): SelectableRegionProps {
  if (!onSelect) return {};
  return {
    role: 'button',
    tabIndex: 0,
    // Without a label the region is announced as everything it contains, which
    // comes out as "Row A Seat 1 0 Section A Adult".
    'aria-label': intl.formatMessage({
      id: "showSeatOnMap",
      defaultMessage: [{
        "type": 0,
        "value": "Show row "
      }, {
        "type": 1,
        "value": "row"
      }, {
        "type": 0,
        "value": " seat "
      }, {
        "type": 1,
        "value": "seat"
      }, {
        "type": 0,
        "value": " on the map"
      }]
    }, {
      row: ticket.row,
      seat: ticket.seat
    }),
    onClick: onSelect,
    onKeyDown: (e: React.KeyboardEvent) => {
      // Keys pressed on a nested control belong to that control.
      if (e.target !== e.currentTarget) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // Suppress the default on every keydown, repeats included, otherwise
      // holding Space scrolls the page. Activate only on the first one, since
      // key repeat would re-run the zoom and ripple for the same seat.
      e.preventDefault();
      if (e.repeat) return;
      onSelect();
    }
  };
}

/** The discount club card applied to a ticket, under a divider. Renders
 *  nothing for a ticket without one. */
const DiscountClubLine = ({
  club,
  mark
}: {
  club: TicketData['discountClub'];
  mark?: React.ReactNode;
}) => club ? <>
      <Divider sx={{
    borderColor: 'other.divider'
  }} />
      <Stack data-testid="ticket-discount-club" direction="row" sx={{
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: 1.5,
    rowGap: 0.5
  }}>
        {mark ?? <Typography variant="body3Medium" sx={{
      color: 'text.secondary'
    }}>
            {club.name}
          </Typography>}
        <Typography variant="body3" sx={{
      color: 'text.secondary'
    }}>
          <FormattedMessage id="ticket.discountClub.applied" defaultMessage={[{
        "type": 0,
        "value": "Discount applied to this ticket"
      }]} />
        </Typography>
        {club.card && <Typography variant="body3Medium" sx={{
      color: 'text.secondary'
    }}>
            {club.card}
          </Typography>}
      </Stack>
    </> : null;
export interface TicketCardProps {
  ticket: TicketData;
  ticketTypeOptions?: TicketTypeOption[];
  editable?: boolean;
  disablePriceZoneIndicator?: boolean;
  showCustomerInfo?: boolean;
  seatNotice?: string;
  isMobile?: boolean;
  onRemoveTicket?: () => void;
  onChangeTicketType?: (encryptedTicketId: string, ticketTypeId: number) => void;
  currencyData?: CurrencyData;
  onShow360View?: () => void;
  /**
   * Makes the seat details an activatable region. The seat picker uses it to
   * zoom the hall to this seat. Left unset the card stays plain content.
   */
  onSelect?: () => void;
  /**
   * Renders a leading checkbox. Set only while the caller is running a
   * multi-select over the tickets; left unset the card has no checkbox.
   */
  onToggleSelect?: () => void;
  selected?: boolean;
  /** Accessible name for the checkbox — the app owns the wording. */
  selectLabel?: string;
  /** The applied discount club's logo. Defaults to the club's name. */
  discountClubMark?: React.ReactNode;
}
export const TicketCard = ({
  ticket,
  ticketTypeOptions,
  editable,
  disablePriceZoneIndicator,
  showCustomerInfo = false,
  seatNotice,
  isMobile,
  onRemoveTicket,
  onChangeTicketType,
  currencyData,
  onShow360View,
  onSelect,
  onToggleSelect,
  selected = false,
  selectLabel,
  discountClubMark
}: TicketCardProps) => {
  const intl = useIntl();
  const selectProps = selectableRegionProps(onSelect, intl, ticket);
  // Controls inside the region drive their own action, so they must not also
  // select the card. Left unbound on a card that is not selectable, so other
  // callers keep their own click bubbling.
  const stopSelect = onSelect ? stopPropagation : undefined;
  const canChangeType = editable && ticketTypeOptions !== undefined && ticketTypeOptions.length > 0 && ticket.ticketTypeId !== undefined;
  const seatInfo = ticket.isNumbered ? <RowSeatInfo row={ticket.row} seat={ticket.seat} /> : <Typography variant="body2Medium">
      <FormattedMessage id="common.unnumbered" defaultMessage={[{
      "type": 0,
      "value": "Unnumbered"
    }]} />
    </Typography>;
  const hasDiscount = ticket.originalPriceWithFeesAndTax != null && ticket.priceWithFeesAndTax != null && Math.round(ticket.originalPriceWithFeesAndTax * 100) !== Math.round(ticket.priceWithFeesAndTax * 100);
  const priceDisplay = <Stack direction="row" spacing={0.5} sx={{
    alignItems: 'center'
  }}>
      {!disablePriceZoneIndicator && ticket.priceZoneColor && <Bullet color={ticket.priceZoneColor} />}
      {hasDiscount && <StrikethroughText>
          <FormattedPrice amount={ticket.originalPriceWithFeesAndTax ?? 0} currencyData={currencyData} />
        </StrikethroughText>}
      <Typography variant="body2Medium">
        <FormattedPrice amount={ticket.priceWithFeesAndTax ?? 0} currencyData={currencyData} />
      </Typography>
    </Stack>;
  const ticketTypeDisplay = canChangeType ? <TicketTypeSelector selectedTicketType={ticket.ticketTypeId ?? -1} setSelectedTicketType={ticketTypeId => onChangeTicketType?.(ticket.encryptedId ?? '', ticketTypeId)} ticketTypeOptions={ticketTypeOptions ?? []} /> : <Typography variant="body3" sx={{
    my: 0.25
  }}>
      {ticket.ticketTypeName}
    </Typography>;
  const show360Button = !!ticket.path360 && onShow360View && <Button size="small" variant="outlined" color="monochrome" onClick={onShow360View} aria-label={intl.formatMessage({
    id: "common.view360",
    defaultMessage: [{
      "type": 0,
      "value": "View 360"
    }]
  })} sx={{
    minWidth: 'auto'
  }}>
      <Camera360Icon sx={{
      fontSize: 16
    }} />
    </Button>;
  const removeButton = editable && onRemoveTicket && <TixlyIconButton color="monochrome" variant="contained" size="small" sx={{
    color: 'action.active',
    backgroundColor: 'transparent',
    '&:hover': {
      color: 'action.active'
    }
  }} onClick={onRemoveTicket} aria-label={intl.formatMessage({
    id: "common.remove",
    defaultMessage: [{
      "type": 0,
      "value": "Remove"
    }]
  })}>
      <CrossIcon />
    </TixlyIconButton>;
  const seatNoticeSection = seatNotice && <>
      <Divider sx={{
      borderColor: 'other.divider'
    }} />
      <Stack direction="row" spacing={1.5} sx={{
      alignItems: 'center'
    }}>
        <SeatIcon sx={{
        fontSize: 16,
        color: 'text.secondary'
      }} />
        <Typography variant="body3" sx={{
        color: 'text.secondary'
      }}>
          {seatNotice}
        </Typography>
      </Stack>
    </>;
  const discountClubSection = <DiscountClubLine club={ticket.discountClub} mark={discountClubMark} />;
  if (isMobile) {
    const mobileContent = <Box {...selectProps} sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      position: 'relative',
      cursor: onSelect ? 'pointer' : undefined
    }}>
        {/* Remove button - absolute top-right on mobile */}
        {editable && onRemoveTicket && <Box sx={{
        position: 'absolute',
        top: 8,
        right: 8
      }} onClick={stopSelect}>
            {removeButton}
          </Box>}

        {/* Customer name */}
        {showCustomerInfo && ticket.customerId !== AnonymousCustomerId && ticket.customerName && <Typography variant="body2Medium" sx={{
        fontWeight: 700
      }}>
              {ticket.customerName}
            </Typography>}

        {/* Seat info and section/ticket type */}
        <Stack sx={{
        gap: 0.5
      }}>
          {seatInfo}
          {(ticket.section || ticket.priceZoneName) && <Typography variant="body3">
              {ticket.section ?? ticket.priceZoneName}
            </Typography>}
        </Stack>

        {/* Bottom row */}
        <Stack direction="row" sx={{
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        pt: canChangeType ? 1.5 : 0.5
      }}>
          <Stack direction="row" sx={{
          gap: 0.5,
          alignItems: 'center'
        }} onClick={stopSelect}>
            {show360Button}
            {ticketTypeDisplay}
          </Stack>
          {priceDisplay}
        </Stack>
      </Box>;
    return <Stack sx={{
      p: 2,
      gap: 2,
      backgroundColor: theme => theme.palette.containerBackground.main,
      borderRadius: theme => `${theme.formItemRadius.main}px`
    }}>
        <Box sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start'
      }}>
          <SelectionCheckbox onToggle={onToggleSelect} selected={selected} label={selectLabel} />
          {mobileContent}
        </Box>
        {seatNoticeSection}
        {discountClubSection}
      </Stack>;
  }

  // Desktop layout
  return <Stack sx={{
    p: 2,
    gap: 2,
    backgroundColor: theme => theme.palette.containerBackground.main,
    borderRadius: theme => `${theme.formItemRadius.main}px`
  }}>
      <Box sx={{
      display: 'flex',
      gap: 1.5,
      alignItems: 'center'
    }}>
        <SelectionCheckbox onToggle={onToggleSelect} selected={selected} label={selectLabel} />
        {/* Content area */}
        <Stack {...selectProps} sx={{
        flex: 1,
        gap: 0.5,
        cursor: onSelect ? 'pointer' : undefined
      }}>
          {/* Customer name */}
          {showCustomerInfo && ticket.customerId !== AnonymousCustomerId && ticket.customerName && <Typography variant="body2Medium" sx={{
          fontWeight: 700
        }}>
                {ticket.customerName}
              </Typography>}
          {/* Row 1: Seat info + price */}
          <Stack direction="row" sx={{
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
            {seatInfo}
            {priceDisplay}
          </Stack>

          {/* Row 2: section/price-zone + ticket type selector */}
          <Stack direction="row" sx={{
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
            <Typography variant="body3">
              {ticket.section ?? ticket.priceZoneName}
            </Typography>
            <Stack direction="row" sx={{
            gap: 0.5,
            alignItems: 'center'
          }} onClick={stopSelect}>
              {show360Button}
              {ticketTypeDisplay}
            </Stack>
          </Stack>
        </Stack>

        {/* Remove button */}
        {removeButton}
      </Box>
      {seatNoticeSection}
      {discountClubSection}
    </Stack>;
};